import React, { useState } from "react";
import { Row, Col, Card, DatePicker, Button, message, Table, Tag } from "antd";
import {
  CalendarOutlined,
  DownloadOutlined,
  CloudUploadOutlined
} from "@ant-design/icons";
import AdminBreadCrumb from "components/AdminBreadCrumb";
import moment from "moment";
import request from "helper/request";
import "./SftpPodReport.scss";

const { RangePicker } = DatePicker;

const breadCrumbs = [{ text: "SFTP POD UPLOAD LOGS" }];

const PAGE_SIZE = 20;

/**
 * Client-facing version of the admin SFTP POD report. The backend scopes every
 * row to the caller's client from the JWT, so there is no client filter here.
 */
const SftpPodReport = () => {
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [dateRange, setDateRange] = useState([
    moment().subtract(1, "month").startOf("month"),
    moment().endOf("month")
  ]);

  const [tableData, setTableData] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Track the last submitted filters so pagination uses the same params
  const [submittedFilters, setSubmittedFilters] = useState(null);

  const fetchPage = async (filters, page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: filters.startDate,
        endDate: filters.endDate,
        page,
        limit: PAGE_SIZE
      });

      const data = await request.get(`/reports/sftp-pod-logs?${params.toString()}`);
      setTableData(data?.data || []);
      setTotal(data?.total || 0);
      setCurrentPage(page);
    } catch {
      message.error("Failed to load SFTP report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.warning("Please select a date range");
      return;
    }

    const filters = {
      startDate: dateRange[0].format("YYYY-MM-DD"),
      endDate: dateRange[1].format("YYYY-MM-DD")
    };

    setSubmittedFilters(filters);
    await fetchPage(filters, 1);
  };

  const handleTableChange = (pagination) => {
    if (!submittedFilters) return;
    fetchPage(submittedFilters, pagination.current);
  };

  const handleExportCSV = async () => {
    if (!submittedFilters) {
      message.warning("Please generate a report first");
      return;
    }

    setExportLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: submittedFilters.startDate,
        endDate: submittedFilters.endDate
      });

      const rows = await request.get(
        `/reports/sftp-pod-logs/export?${params.toString()}`
      );

      if (!rows || rows.length === 0) {
        message.warning("No data to export");
        return;
      }

      const filename = `sftp-pod-logs-${submittedFilters.startDate}_${submittedFilters.endDate}.csv`;

      const headers = [
        "ID",
        "Created At",
        "Updated At",
        "Route Location ID",
        "Job ID",
        "Success",
        "Error Message",
        "Remote Path"
      ];

      const escape = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;

      const csvRows = [
        headers.join(","),
        ...rows.map((row) =>
          [
            escape(row.id),
            escape(
              row.createdAt ? moment(row.createdAt).format("YYYY-MM-DD HH:mm:ss") : ""
            ),
            escape(
              row.updatedAt ? moment(row.updatedAt).format("YYYY-MM-DD HH:mm:ss") : ""
            ),
            escape(row.routeLocationId),
            escape(row.jobId),
            escape(row.success ? "Yes" : "No"),
            escape(row.errorMessage),
            escape(row.remotePath)
          ].join(",")
        )
      ];

      const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.setAttribute("href", URL.createObjectURL(blob));
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Exported ${rows.length} records`);
    } catch {
      message.error("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const columns = [
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 175,
      render: (val) => (val ? moment(val).format("YYYY-MM-DD HH:mm:ss") : "-")
    },
    {
      title: "Success",
      dataIndex: "success",
      key: "success",
      width: 90,
      render: (val) =>
        val ? <Tag color="success">Yes</Tag> : <Tag color="error">No</Tag>
    },
    {
      title: "Remote Path",
      dataIndex: "remotePath",
      key: "remotePath",
      width: 250,
      render: (val) => val || "-",
      ellipsis: true
    },
    {
      title: "Error Message",
      dataIndex: "errorMessage",
      key: "errorMessage",
      render: (val) => val || "-",
      ellipsis: true
    },
    {
      title: "Job ID",
      dataIndex: "jobId",
      key: "jobId",
      width: 110,
      render: (val) => (val ? val.substring(0, 8).toUpperCase() : "-")
    },
    {
      title: "Route Location ID",
      dataIndex: "routeLocationId",
      key: "routeLocationId",
      width: 160,
      render: (val) => (val ? val.substring(0, 8).toUpperCase() : "-")
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 175,
      render: (val) => (val ? moment(val).format("YYYY-MM-DD HH:mm:ss") : "-")
    }
  ];

  return (
    <div className="sftp-pod-report">
      <AdminBreadCrumb breadCrumbs={breadCrumbs} />

      <div className="sftp-pod-report-content">
        <div className="sftp-pod-report-header">
          <h1 style={{ color: "#1890ff", marginBottom: "8px" }}>SFTP POD Upload Logs</h1>
          <p style={{ color: "#666", marginBottom: "32px" }}>
            View and export your SFTP Proof-of-Delivery upload log entries
          </p>
        </div>

        {/* Filters */}
        <Card className="filters-card" style={{ marginBottom: "24px" }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={16}>
              <div style={{ marginBottom: "8px" }}>
                <CalendarOutlined
                  style={{ color: "#1890ff", fontSize: "14px", marginRight: "8px" }}
                />
                <span style={{ fontWeight: 500 }}>Date Range *</span>
              </div>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
                placeholder={["Start Date", "End Date"]}
                style={{ width: "100%" }}
                disabledDate={(current) => current && current > moment().endOf("day")}
              />
            </Col>

            <Col xs={24} md={8} style={{ display: "flex", alignItems: "flex-end" }}>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={loading}
                disabled={!dateRange || dateRange.length !== 2}
                icon={<CloudUploadOutlined />}
                style={{ width: "100%" }}
              >
                Generate
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Results */}
        {submittedFilters && (
          <div className="report-results">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px"
              }}
            >
              <h2 style={{ color: "#1890ff", margin: 0 }}>
                {moment(submittedFilters.startDate).format("MMM DD, YYYY")} &mdash;{" "}
                {moment(submittedFilters.endDate).format("MMM DD, YYYY")}
                <span style={{ color: "#666", fontSize: "16px", marginLeft: "12px" }}>
                  ({total} record{total !== 1 ? "s" : ""})
                </span>
              </h2>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportCSV}
                loading={exportLoading}
                disabled={total === 0}
              >
                Export CSV
              </Button>
            </div>

            <Card>
              <Table
                columns={columns}
                dataSource={tableData}
                rowKey="id"
                loading={loading}
                scroll={{ x: 1200 }}
                pagination={{
                  current: currentPage,
                  pageSize: PAGE_SIZE,
                  total,
                  showTotal: (t) => `Total ${t} records`,
                  showSizeChanger: false
                }}
                onChange={handleTableChange}
              />
            </Card>
          </div>
        )}

        {!submittedFilters && !loading && (
          <Card className="empty-state">
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <CloudUploadOutlined
                style={{ fontSize: "48px", color: "#d9d9d9", marginBottom: "16px" }}
              />
              <h3 style={{ color: "#666" }}>No Report Generated</h3>
              <p style={{ color: "#999" }}>Select a date range, then click Generate</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SftpPodReport;
